<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">数据质量管理</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">数据治理知识库</span>
          <span class="tag">数据质量</span>
          <span class="tag">DQM</span>
        </div>
        <h1 class="article-title">数据质量管理</h1>
        <p class="article-subtitle">评估框架 · 质量规则库 · 根因分析 · 整改闭环 · PDCA持续改进</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2025-12-25</span>
          <span><i class="far fa-clock"></i> 阅读约 16 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">✅</div>
        <div>
          <h3>导读</h3>
          <p>数据质量是数据治理的核心目标——所有治理工作最终都要用"数据是否变好了"来衡量成效。本知识库覆盖数据质量管理的全链条：从评估框架和六维度模型，到300+质量规则库，再到问题根因分析和PDCA整改闭环，帮助企业建立可度量、可追踪、可持续的数据质量管理体系。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、数据质量为何重要</a></li>
          <li><a href="#section2">二、六维度质量评估模型</a></li>
          <li><a href="#section3">三、质量规则库建设</a></li>
          <li><a href="#section4">四、质量问题根因分析</a></li>
          <li><a href="#section5">五、PDCA质量改进闭环</a></li>
          <li><a href="#section6">六、数据质量监控平台</a></li>
          <li><a href="#section7">七、质量治理实战案例</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、数据质量为何重要</h2>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">💡</div>
              <div>
                <h4>Garbage In, Garbage Out</h4>
                <p>如果源头数据质量不合格，无论后续的分析模型多先进、BI报表多精美，产出的结论都是不可信的。数据质量是整个数据价值链的"地基"。</p>
              </div>
            </div>
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">$12.9M</div>
              <div class="stat-label">Gartner：数据质量低劣每年平均给企业造成1290万美元损失</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">27%</div>
              <div class="stat-label">的企业数据存在严重质量问题（IBM调查）</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">60%</div>
              <div class="stat-label">的分析师时间用于数据清洗而非分析</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">3倍</div>
              <div class="stat-label">修复下游质量问题的成本是源头修复的3倍</div>
            </div>
          </div>

          <h3>数据质量问题的业务影响</h3>
          <div class="impact-grid">
            <div class="impact-card" v-for="imp in impacts" :key="imp.scene">
              <div class="impact-icon">{{ imp.icon }}</div>
              <h4>{{ imp.scene }}</h4>
              <p>{{ imp.desc }}</p>
              <div class="impact-loss">损失：{{ imp.loss }}</div>
            </div>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、六维度质量评估模型</h2>
          <p>国际通行的数据质量评估框架将质量度量分为六个核心维度，每个维度有明确的度量方法和目标值。</p>

          <div class="dim-cards">
            <div class="dim-card" v-for="dim in sixDimensions" :key="dim.name" :style="{ borderTopColor: dim.color }">
              <div class="dim-header">
                <span class="dim-icon">{{ dim.icon }}</span>
                <h4>{{ dim.name }}</h4>
              </div>
              <p class="dim-definition">{{ dim.definition }}</p>
              <div class="dim-detail">
                <div class="dim-row">
                  <span class="dim-label">度量方法</span>
                  <span class="dim-value">{{ dim.measure }}</span>
                </div>
                <div class="dim-row">
                  <span class="dim-label">典型规则</span>
                  <span class="dim-value">{{ dim.rule }}</span>
                </div>
                <div class="dim-row">
                  <span class="dim-label">目标值</span>
                  <span class="dim-value target">{{ dim.target }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>不同业务场景的质量侧重点</h4>
              <p>财务数据侧重"准确性"和"一致性"；客户数据侧重"唯一性"和"完整性"；生产数据侧重"及时性"和"有效性"。不必在所有维度追求极致，找到业务最关心的维度优先提升。</p>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、质量规则库建设</h2>

          <h3>3.1 规则分类体系</h3>
          <div class="rule-categories">
            <div class="rule-cat" v-for="cat in ruleCategories" :key="cat.name">
              <div class="rule-cat-header" :style="{ background: cat.color }">
                <span>{{ cat.icon }}</span>
                <h4>{{ cat.name }}</h4>
              </div>
              <div class="rule-cat-body">
                <p>{{ cat.desc }}</p>
                <ul>
                  <li v-for="(rule, i) in cat.rules" :key="i">{{ rule }}</li>
                </ul>
                <div class="rule-count">规则数量：{{ cat.count }}+</div>
              </div>
            </div>
          </div>

          <h3>3.2 规则实施优先级</h3>
          <div class="priority-matrix">
            <table>
              <thead>
                <tr>
                  <th>优先级</th>
                  <th>规则类型</th>
                  <th>适用场景</th>
                  <th>实施建议</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in priorities" :key="p.level">
                  <td><span class="priority-badge" :style="{ background: p.color }">{{ p.level }}</span></td>
                  <td>{{ p.type }}</td>
                  <td>{{ p.scene }}</td>
                  <td>{{ p.advice }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、质量问题根因分析</h2>

          <h3>4.1 常见根因分类</h3>
          <div class="rootcause-grid">
            <div class="rootcause-card" v-for="rc in rootCauses" :key="rc.category">
              <div class="rc-icon">{{ rc.icon }}</div>
              <h4>{{ rc.category }}</h4>
              <ul>
                <li v-for="(cause, i) in rc.causes" :key="i">{{ cause }}</li>
              </ul>
              <div class="rc-ratio">占比约 {{ rc.ratio }}</div>
            </div>
          </div>

          <h3>4.2 鱼骨图分析法</h3>
          <div class="fishbone">
            <div class="fishbone-center">
              <span>数据质量问题</span>
            </div>
            <div class="fishbone-branches">
              <div class="fishbone-branch" v-for="branch in fishboneBranches" :key="branch.name">
                <div class="branch-name">{{ branch.name }}</div>
                <div class="branch-items">
                  <span v-for="(item, i) in branch.items" :key="i">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🎯</div>
              <div>
                <h4>根因分析的核心原则</h4>
                <p>不要停留在"数据录入错误"这样的表面原因。深挖五个"为什么"：为什么录入错误？→ 没有校验规则。为什么没有校验？→ 系统不支持。为什么不支持？→ 没有提需求。为什么没提？→ 没有质量意识……找到真正的根因，才能从根本上解决问题。</p>
              </div>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、PDCA质量改进闭环</h2>

          <div class="pdca-cycle">
            <div class="pdca-step" v-for="step in pdcaSteps" :key="step.name" :style="{ background: step.color }">
              <div class="pdca-letter">{{ step.letter }}</div>
              <h4>{{ step.name }}</h4>
              <p>{{ step.desc }}</p>
              <div class="pdca-tasks">
                <span v-for="(task, i) in step.tasks" :key="i">{{ task }}</span>
              </div>
            </div>
          </div>

          <h3>质量改进工单流程</h3>
          <div class="ticket-flow">
            <div class="ticket-step" v-for="(step, i) in ticketFlow" :key="i">
              <div class="ticket-marker" :style="{ background: step.color }">{{ i + 1 }}</div>
              <div class="ticket-content">
                <h4>{{ step.title }}</h4>
                <p>{{ step.desc }}</p>
                <div class="ticket-sla">SLA：{{ step.sla }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、数据质量监控平台</h2>

          <h3>6.1 平台核心功能</h3>
          <div class="platform-features">
            <div class="pf-card" v-for="f in platformFeatures" :key="f.name">
              <div class="pf-icon">{{ f.icon }}</div>
              <h4>{{ f.name }}</h4>
              <p>{{ f.desc }}</p>
            </div>
          </div>

          <h3>6.2 质量看板核心指标</h3>
          <div class="dashboard-grid">
            <div class="dashboard-card" v-for="d in dashboardItems" :key="d.name">
              <div class="dash-value" :style="{ color: d.color }">{{ d.value }}</div>
              <div class="dash-name">{{ d.name }}</div>
              <div class="dash-trend">{{ d.trend }}</div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>从"事后补救"到"事前预防"</h4>
              <p>优秀的质量管理平台不仅能发现问题，更要能预防问题。通过在数据入口端嵌入实时校验规则，在ETL管道中设置质量关卡，实现"脏数据进不来、坏数据出不去"的防线。</p>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、质量治理实战案例</h2>

          <div class="case-cards">
            <div class="case-card" v-for="c in cases" :key="c.name">
              <div class="case-header" :style="{ background: c.bg }">
                <h3>{{ c.name }}</h3>
                <p class="case-industry">{{ c.industry }}</p>
              </div>
              <div class="case-body">
                <div class="case-row">
                  <span class="case-label">核心问题</span>
                  <span>{{ c.problem }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">治理方案</span>
                  <span>{{ c.solution }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">量化成果</span>
                  <span class="case-result">{{ c.result }}</span>
                </div>
                <div class="case-row">
                  <span class="case-label">治理周期</span>
                  <span>{{ c.duration }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>结语</h4>
                <p>数据质量管理不是一次性项目，而是一个持续运营的过程。建立"度量 → 发现 → 分析 → 整改 → 验证"的闭环机制，让质量像安全一样成为组织的"肌肉记忆"。当企业的数据质量合格率从60%提升到95%，你会真切感受到数据驱动决策的力量。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>需要数据质量治理咨询？</h2>
        <p>古杉数据提供全套数据质量评估、规则库建设和监控平台解决方案</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约质量治理专家</router-link>
          <router-link to="/consulting/data-governance" class="btn-outline">了解数据治理咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'DataQualityManagement',
  data() {
    return {
      impacts: [
        { icon: '📊', scene: '经营决策失误', desc: '基于错误数据做出的投资、定价、扩张决策', loss: '可达千万级' },
        { icon: '🏦', scene: '监管报送违规', desc: '向监管机构报送的数据不准确，面临处罚', loss: '罚款 + 声誉损失' },
        { icon: '🔄', scene: '系统集成失败', desc: '新系统上线时因数据质量差导致上线延迟', loss: '项目延期3-6个月' },
        { icon: '🤖', scene: 'AI模型无效', desc: '训练数据质量差导致模型效果远低于预期', loss: '研发投入浪费' }
      ],
      sixDimensions: [
        { icon: '✅', name: '准确性', definition: '数据值是否正确反映了实际的业务状态', measure: '错误记录数 / 总记录数', rule: '手机号格式、金额范围、日期逻辑校验', target: '≥ 99%', color: '#10B981' },
        { icon: '📋', name: '完整性', definition: '必要的数据字段是否完整填写', measure: '空值字段数 / 应填字段总数', rule: '必填字段非空检查、关联字段完整性', target: '≥ 98%', color: '#3B82F6' },
        { icon: '🔗', name: '一致性', definition: '同一数据在不同系统中是否一致', measure: '不一致记录数 / 跨系统比对总数', rule: 'ERP-CRM客户信息对比、主从数据对比', target: '≥ 95%', color: '#F59E0B' },
        { icon: '⏱', name: '及时性', definition: '数据是否在规定时间内更新', measure: '超时更新数 / 应更新总数', rule: '实时数据延迟监控、批量数据SLA检查', target: '≥ 97%', color: '#8B5CF6' },
        { icon: '🚫', name: '唯一性', definition: '是否存在重复记录', measure: '重复记录数 / 总记录数', rule: '主键唯一检查、业务键去重检测', target: '≤ 0.5%', color: '#DC2626' },
        { icon: '📐', name: '有效性', definition: '数据值是否在合理的值域范围内', measure: '越界记录数 / 总记录数', rule: '枚举值检查、范围校验、引用完整性', target: '≥ 99%', color: '#EA580C' }
      ],
      ruleCategories: [
        { icon: '📏', name: '格式规则', desc: '检查数据格式是否符合约定', color: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', count: '60', rules: ['手机号11位数字格式', '邮编6位数字', '日期格式YYYY-MM-DD', '金额保留2位小数'] },
        { icon: '📊', name: '范围规则', desc: '检查数据值是否在合理范围内', color: 'linear-gradient(135deg, #10B981, #059669)', count: '50', rules: ['年龄0-150岁', '折扣率0-100%', '温度-50℃~60℃', '订单金额>0'] },
        { icon: '🔗', name: '关联规则', desc: '检查数据之间的逻辑关系', color: 'linear-gradient(135deg, #F59E0B, #D97706)', count: '80', rules: ['发票金额≤合同金额', '出库日期≥入库日期', '子项之和=父项合计', '外键引用完整'] },
        { icon: '🔍', name: '业务规则', desc: '基于业务逻辑的复合校验', color: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', count: '100+', rules: ['已注销客户不可有新订单', 'VIP客户必填联系电话', '已审批单据不可修改金额', '库存量≥安全库存触发预警'] }
      ],
      priorities: [
        { level: 'P0', type: '阻断性规则', scene: '数据入库前必须通过', advice: '第一期上线，硬拦截', color: '#DC2626' },
        { level: 'P1', type: '核心业务规则', scene: '影响报表、决策的关键数据', advice: '第一期上线，告警+工单', color: '#EA580C' },
        { level: 'P2', type: '一般质量规则', scene: '影响体验但不影响核心业务', advice: '第二期上线，定期巡检', color: '#F59E0B' },
        { level: 'P3', type: '优化建议规则', scene: '提升数据完善度', advice: '第三期上线，建议修复', color: '#10B981' }
      ],
      rootCauses: [
        { icon: '👤', category: '人为因素', causes: ['录入错误（拼写、数值）', '缺乏操作规范培训', '责任心不强、考核不到位', '跨部门沟通不畅'], ratio: '35%' },
        { icon: '🖥️', category: '系统因素', causes: ['缺少前端校验规则', '系统间接口数据丢失', '自动化脚本逻辑错误', '数据库约束缺失'], ratio: '30%' },
        { icon: '📋', category: '流程因素', causes: ['数据创建流程不规范', '审核环节缺失', '变更通知机制不健全', '缺少数据清洗环节'], ratio: '25%' },
        { icon: '🏢', category: '组织因素', causes: ['没有数据治理组织', '数据责任人不明确', '部门间数据割裂', '缺少质量考核机制'], ratio: '10%' }
      ],
      fishboneBranches: [
        { name: '人 People', items: ['录入错误', '培训不足', '责任不清'] },
        { name: '机 System', items: ['校验缺失', '接口异常', '约束不足'] },
        { name: '料 Data', items: ['源头脏数据', '格式不统一', '编码冲突'] },
        { name: '法 Process', items: ['流程不规范', '审核缺位', '变更失控'] },
        { name: '环 Environment', items: ['组织割裂', '考核缺失', '工具不全'] }
      ],
      pdcaSteps: [
        { letter: 'P', name: 'Plan 计划', desc: '识别质量问题，确定改进目标和方案', color: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', tasks: ['质量评估', '问题排序', '制定目标', '编写方案'] },
        { letter: 'D', name: 'Do 执行', desc: '按计划实施质量改进措施', color: 'linear-gradient(135deg, #10B981, #059669)', tasks: ['部署规则', '数据清洗', '流程改进', '系统优化'] },
        { letter: 'C', name: 'Check 检查', desc: '评估改进效果，对比目标', color: 'linear-gradient(135deg, #F59E0B, #D97706)', tasks: ['效果度量', '目标对比', '问题复盘', '报告编写'] },
        { letter: 'A', name: 'Act 处理', desc: '固化成果或调整方案进入下一轮', color: 'linear-gradient(135deg, #DC2626, #991B1B)', tasks: ['标准固化', '经验沉淀', '问题转移', '下轮计划'] }
      ],
      ticketFlow: [
        { title: '问题发现', desc: '质量规则引擎自动扫描产出质量问题清单', color: '#4A90D9', sla: '自动实时' },
        { title: '工单生成', desc: '自动创建质量整改工单，分派给数据责任人', color: '#1F6FEB', sla: 'T+0 自动' },
        { title: '根因分析', desc: '数据责任人分析问题根因，填写分析报告', color: '#0E4EAD', sla: '24小时内' },
        { title: '整改执行', desc: '按整改方案修复数据并解决根本原因', color: '#0A3A82', sla: '48小时内' },
        { title: '验证关闭', desc: '再次运行质量规则验证，确认通过后关闭工单', color: '#0E1F52', sla: '72小时内' }
      ],
      platformFeatures: [
        { icon: '📏', name: '规则管理', desc: '可视化规则配置、分类管理、版本控制' },
        { icon: '⚡', name: '自动扫描', desc: '定时或实时运行质量规则，输出检测报告' },
        { icon: '🔔', name: '告警通知', desc: '质量异常自动告警，支持邮件/企业微信/钉钉' },
        { icon: '📋', name: '工单管理', desc: '质量问题工单全生命周期管理' },
        { icon: '📊', name: '质量看板', desc: '多维度质量指标可视化，趋势分析' },
        { icon: '📈', name: '报告输出', desc: '自动生成周/月质量报告，支持导出' }
      ],
      dashboardItems: [
        { name: '综合质量得分', value: '92.3', color: '#10B981', trend: '↑ 较上月+2.1' },
        { name: '准确率', value: '99.2%', color: '#3B82F6', trend: '↑ +0.3%' },
        { name: '完整率', value: '97.8%', color: '#1A73E8', trend: '↑ +1.2%' },
        { name: '一致率', value: '95.6%', color: '#F59E0B', trend: '↑ +0.8%' },
        { name: '待处理工单', value: '23', color: '#DC2626', trend: '↓ 较上月-12' },
        { name: '工单平均闭环时长', value: '18h', color: '#8B5CF6', trend: '↓ 较上月-6h' }
      ],
      cases: [
        {
          name: '某央企集团', industry: '能源行业', bg: 'linear-gradient(135deg, #10B981, #059669)',
          problem: '集团70+子公司数据口径不统一，月度经营报表存在上百处差异',
          solution: '部署数据质量管理平台，建立800+质量规则，实施三级质量巡检体系',
          result: '数据质量综合得分从62分提升至93分，报表差异降低96%',
          duration: '8个月'
        },
        {
          name: '某商业银行', industry: '金融行业', bg: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
          problem: '监管报送数据频繁被退回，1年内收到3次监管关注函',
          solution: '建立监管数据质量专项治理，覆盖EAST、1104等核心报表',
          result: '监管报送数据准确率从94%提升至99.97%，0次退回',
          duration: '6个月'
        },
        {
          name: '某制造企业', industry: '装备制造', bg: 'linear-gradient(135deg, #F59E0B, #D97706)',
          problem: '物料主数据重复率18%，BOM准确率不足75%，影响生产计划',
          solution: '主数据清洗+质量规则引擎+数据准入流程改造',
          result: '物料重复率降至0.3%，BOM准确率提升至98%',
          duration: '10个月'
        }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 1.8rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 影响 */
.impact-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.impact-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.impact-icon { font-size: 2rem; margin-bottom: 8px; }
.impact-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.impact-card p { font-size: 13px; color: #5A5E66; margin-bottom: 8px; line-height: 1.5; }
.impact-loss { font-size: 13px; color: #DC2626; font-weight: 600; }

/* 六维度 */
.dim-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.dim-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; border-top: 3px solid; }
.dim-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.dim-icon { font-size: 1.5rem; }
.dim-header h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin: 0; }
.dim-definition { font-size: 14px; color: #3D4150; margin-bottom: 12px; line-height: 1.5; }
.dim-detail { border-top: 1px solid #E8ECF0; padding-top: 12px; }
.dim-row { display: flex; gap: 8px; font-size: 13px; margin-bottom: 6px; }
.dim-label { color: #5A5E66; font-weight: 600; min-width: 65px; }
.dim-value { color: #3D4150; }
.dim-value.target { color: #10B981; font-weight: 700; }

/* 规则分类 */
.rule-categories { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.rule-cat { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.rule-cat-header { padding: 16px 20px; color: #fff; display: flex; align-items: center; gap: 8px; }
.rule-cat-header span { font-size: 1.5rem; }
.rule-cat-header h4 { font-size: 16px; font-weight: 700; margin: 0; }
.rule-cat-body { padding: 20px; }
.rule-cat-body p { font-size: 14px; color: #5A5E66; margin-bottom: 10px; }
.rule-cat-body ul { list-style: none; padding: 0; margin: 0 0 10px; }
.rule-cat-body li { font-size: 13px; color: #3D4150; padding: 3px 0 3px 14px; position: relative; }
.rule-cat-body li::before { content: '▸'; position: absolute; left: 0; color: #1A73E8; font-weight: 700; }
.rule-count { font-size: 12px; color: #1A73E8; font-weight: 600; }

/* 优先级 */
.priority-matrix { margin: 24px 0; overflow-x: auto; }
.priority-matrix table { width: 100%; border-collapse: collapse; font-size: 14px; }
.priority-matrix th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.priority-matrix td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.priority-matrix tr:nth-child(even) td { background: #F6F8FC; }
.priority-badge { color: #fff; padding: 4px 12px; border-radius: 6px; font-size: 13px; font-weight: 700; }

/* 根因 */
.rootcause-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 24px 0; }
.rootcause-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.rc-icon { font-size: 2rem; margin-bottom: 8px; }
.rootcause-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 10px; }
.rootcause-card ul { list-style: none; padding: 0; margin: 0 0 10px; }
.rootcause-card li { font-size: 13px; color: #5A5E66; padding: 3px 0 3px 14px; position: relative; }
.rootcause-card li::before { content: '•'; position: absolute; left: 0; color: #1A73E8; font-weight: 700; }
.rc-ratio { font-size: 13px; color: #DC2626; font-weight: 600; }

/* 鱼骨 */
.fishbone { background: #F6F8FC; border-radius: 12px; padding: 32px; margin: 24px 0; border: 1px solid #E8ECF0; }
.fishbone-center { text-align: center; margin-bottom: 20px; }
.fishbone-center span { background: #DC2626; color: #fff; padding: 10px 24px; border-radius: 8px; font-size: 16px; font-weight: 700; }
.fishbone-branches { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.fishbone-branch { text-align: center; }
.branch-name { font-size: 14px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.branch-items { display: flex; flex-direction: column; gap: 4px; }
.branch-items span { background: #EBF3FE; color: #1A73E8; padding: 4px 8px; border-radius: 6px; font-size: 12px; }

/* PDCA */
.pdca-cycle { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 24px 0; }
.pdca-step { border-radius: 12px; padding: 24px; color: #fff; }
.pdca-letter { font-size: 2.5rem; font-weight: 900; opacity: 0.3; margin-bottom: 8px; }
.pdca-step h4 { font-size: 16px; font-weight: 700; margin-bottom: 6px; }
.pdca-step > p { font-size: 13px; opacity: 0.85; margin-bottom: 12px; line-height: 1.5; }
.pdca-tasks { display: flex; flex-wrap: wrap; gap: 6px; }
.pdca-tasks span { background: rgba(255,255,255,0.2); padding: 3px 10px; border-radius: 6px; font-size: 12px; }

/* 工单 */
.ticket-flow { position: relative; padding-left: 40px; margin: 24px 0; }
.ticket-flow::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.ticket-step { position: relative; margin-bottom: 20px; }
.ticket-step:last-child { margin-bottom: 0; }
.ticket-marker { position: absolute; left: -40px; width: 38px; height: 38px; border-radius: 50%; color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.ticket-content { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; }
.ticket-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.ticket-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.ticket-sla { font-size: 13px; color: #1A73E8; font-weight: 600; }

/* 平台功能 */
.platform-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.pf-card { background: #F6F8FC; border-radius: 12px; padding: 20px; border: 1px solid #E8ECF0; text-align: center; transition: all 0.3s; }
.pf-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.pf-icon { font-size: 2rem; margin-bottom: 8px; }
.pf-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.pf-card p { font-size: 13px; color: #5A5E66; margin: 0; }

/* 看板 */
.dashboard-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.dashboard-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; text-align: center; }
.dash-value { font-size: 2rem; font-weight: 900; margin-bottom: 4px; }
.dash-name { font-size: 14px; color: #0E1F52; font-weight: 600; margin-bottom: 6px; }
.dash-trend { font-size: 12px; color: #10B981; }

/* 案例 */
.case-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.case-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.case-header { padding: 24px; color: #fff; }
.case-header h3 { font-size: 18px; font-weight: 800; margin-bottom: 4px; }
.case-industry { font-size: 13px; opacity: 0.8; margin: 0; }
.case-body { padding: 20px; }
.case-row { font-size: 13px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #E8ECF0; }
.case-row:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.case-label { display: block; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.case-row span:last-child { color: #5A5E66; line-height: 1.5; }
.case-result { color: #10B981 !important; font-weight: 600; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .impact-grid { grid-template-columns: repeat(2, 1fr); }
  .dim-cards { grid-template-columns: 1fr; }
  .rule-categories { grid-template-columns: 1fr; }
  .rootcause-grid { grid-template-columns: repeat(2, 1fr); }
  .pdca-cycle { grid-template-columns: repeat(2, 1fr); }
  .platform-features { grid-template-columns: 1fr; }
  .dashboard-grid { grid-template-columns: 1fr; }
  .case-cards { grid-template-columns: 1fr; }
  .fishbone-branches { grid-template-columns: repeat(3, 1fr); }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .impact-grid { grid-template-columns: 1fr; }
  .rootcause-grid { grid-template-columns: 1fr; }
  .pdca-cycle { grid-template-columns: 1fr; }
  .fishbone-branches { grid-template-columns: 1fr; }
}
</style>
